import { z } from 'zod';

/**
 * Briques de champs réutilisables entre schémas.
 *
 * Beaucoup de formulaires construisent leur charge utile avec parseInt ou
 * parseFloat sur des états initialisés à null : le résultat est NaN, que
 * JSON.stringify sérialise en null. L'API reçoit donc null par accident plutôt
 * que par intention, et rien ne distingue « non renseigné » de « saisie
 * illisible ».
 *
 * Les briques ci-dessous normalisent cela une bonne fois : NaN, null, undefined
 * et chaîne vide donnent null, et la valeur validée ressort en nombre.
 *
 * À noter : les utilitaires existants integerOrNull et floatOrNull ne couvrent
 * pas la chaîne vide, isNaN('') valant false, et renvoient NaN dans ce cas.
 * Ils sont utilisés ailleurs et ne sont pas modifiés ici.
 */

/**
 * Champ vide donne null ; saisie illisible laissée telle quelle pour que le
 * schéma la refuse.
 *
 * La distinction impose que la conversion se fasse ici et non dans le
 * formulaire : un parseFloat appliqué en amont produit NaN aussi bien pour un
 * champ vide que pour « abc », et les deux cas deviennent alors
 * indiscernables.
 */
const versNombreOuNull = (analyser) => (valeur) => {
  if (valeur === null || valeur === undefined) {
    return null;
  }
  if (typeof valeur === 'string' && valeur.trim() === '') {
    return null;
  }
  // NaN déjà présent en entrée : on ne peut plus rien en déduire, on le
  // ramène à null plutôt que de bloquer.
  if (typeof valeur === 'number' && Number.isNaN(valeur)) {
    return null;
  }
  const nombre = analyser(valeur);
  return Number.isNaN(nombre) ? valeur : nombre;
};

/**
 * Entier facultatif, avec son propre message et des bornes éventuelles.
 * Sert quand « valeur invalide » ne dit pas assez quoi saisir.
 */
export const creerEntierOuNull = ({
  message = 'valeur_invalide',
  min,
  max,
} = {}) => {
  let entier = z.number({ message }).int({ message });
  if (min !== undefined) {
    entier = entier.min(min, { message });
  }
  if (max !== undefined) {
    entier = entier.max(max, { message });
  }
  return z.preprocess(
    versNombreOuNull((v) => parseInt(v, 10)),
    entier.nullable()
  );
};

/** Entier facultatif : null quand le champ n'est pas renseigné. */
export const entierOuNull = creerEntierOuNull();

/**
 * Projet de rattachement, obligatoire. Un identifiant de projet est toujours
 * positif, ce qui écarte aussi le '-1' que FormulaireSelectionProjet utilise
 * pour « aucun projet ».
 */
export const projetObligatoire = creerEntierOuNull({
  message: 'selectionner_un_projet',
  min: 1,
}).refine((valeur) => valeur !== null, { message: 'selectionner_un_projet' });

/** Décimal facultatif : null quand le champ n'est pas renseigné. */
export const decimalOuNull = z.preprocess(
  versNombreOuNull(parseFloat),
  z.number({ message: 'valeur_invalide' }).nullable()
);

/** Texte libre, facultatif, null accepté. */
export const texteFacultatif = z
  .string({ message: 'valeur_invalide' })
  .nullish();
